import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { connect, useSelector } from "react-redux";
import AppHeader from "../AppHeader";
import SelectDropdown from "../SelectDropdown";
import { CircularProgress } from "@mui/material";
import {
  getDropdownOptions,
  toTitleCase,
  getStoreChecker,
  getSalesTrendDropdownOptions,
  getInventoryTrendDropdownOptions,
  getLocationNameWithoutState,
  getLocationStateToken,
} from "../../utils/utils";
import { useLazyQuery } from "@apollo/client";
import { GET_DEFAULT_LOCATION } from "../../graphql/queries";
import ErrorMessage from "../ErrorMessage";
import {
  DATA_FETCH_ERROR,
  ONLOAN_INVALID_STOREID_ERROR,
  ZERO_SALES,
  TREND,
  YESTERDAY,
  LCW,
  MTD,
} from "../../common/constants";
import { SalesTabManager } from "../TabManager";
import { setCurrentDateMatrix } from "../../actions/actions";
import BottomNavigator from "../BottomNavigator/BottomNavigator";
import "./styles.scss";

const customStyles = {
  option: (provided, state) => ({
    ...provided,
    fontSize: "1.6rem",
    color: state.isSelected ? "#FFFFFF" : "#353535",
    padding: 7,
  }),
};

const isTrendOrZeroSalesTab = (activeTab) =>
  activeTab === TREND || activeTab === ZERO_SALES;

const resolveSummaryDetailsDropdownValue = (activeTab) => {
  if (!localStorage.hasOwnProperty("selectedDate")) {
    return isTrendOrZeroSalesTab(activeTab) ? LCW : YESTERDAY;
  }

  const stored = localStorage.getItem("selectedDate");
  if (stored === YESTERDAY && isTrendOrZeroSalesTab(activeTab)) {
    return LCW;
  }
  if (activeTab === ZERO_SALES) {
    return stored === LCW || stored === MTD ? stored : LCW;
  }
  return stored;
};

const SummaryDetails = ({ employeeInfo = {}, currentDateMatrix }) => {
  const [options, setOptions] = React.useState([]);
  const [selectedDate, setSelectedDate] = React.useState(null);
  const userEmail = employeeInfo.adUpn;
  const [selectedLocation, setSelectedLocation] = React.useState(
    JSON.parse(sessionStorage.getItem("selectedLocation"))
  );
  let selectedStoreId = localStorage.getItem("storeId");
  const activeStorechecker = getStoreChecker(selectedStoreId);
  const [activeTab, setActiveTab] = useState("");
  const currentActiveTab = useSelector(
    (state) => state.currentActiveTab.currentActiveTab
  );
  useEffect(() => {
    setActiveTab(currentActiveTab);
  }, [activeTab, currentActiveTab]);

  const [
    getDefaultLocation,
    { error: locationError = {}, loading: locationLoading },
  ] = useLazyQuery(GET_DEFAULT_LOCATION, {
    onCompleted: (data) => {
      let { salesSummaryV2 } = data;
      if (selectedLocation.locationName === "NA") {
        setSelectedLocation({
          locationNo: salesSummaryV2?.location?.primaryLocationNumber,
          locationName: salesSummaryV2?.location?.primaryLocationName,
        });
        sessionStorage.setItem(
          "selectedLocation",
          JSON.stringify({
            locationNo: salesSummaryV2?.location?.primaryLocationNumber,
            locationName: salesSummaryV2?.location?.primaryLocationName,
          })
        );
      }
    },
  });

  useEffect(() => {
    let options = [];
    if (activeTab === TREND) {
      options = getSalesTrendDropdownOptions();
    } else if (activeTab === ZERO_SALES) {
      options = getInventoryTrendDropdownOptions();
    } else {
      options = getDropdownOptions();
    }
    setOptions(options);
    const dropdownVal = resolveSummaryDetailsDropdownValue(activeTab);
    const defaultValue = options.find((obj) => obj.value === dropdownVal);
    if (defaultValue) {
      setSelectedDate(defaultValue);
      currentDateMatrix(defaultValue);
    }
  }, [activeTab, currentDateMatrix]);

  useEffect(() => {
    if (userEmail && (activeStorechecker === true || selectedStoreId === "")) {
      getDefaultLocation({
        variables: { request: { email: userEmail, storeId: selectedStoreId } },
        context: {
          headers: {
            Authorization: localStorage.getItem("jwtToken"),
          },
        },
      });
    }
  }, [userEmail, getDefaultLocation, selectedStoreId, activeStorechecker]);

  const handleDateChange = (e) => {
    setSelectedDate(e);
    currentDateMatrix(e);
    localStorage.setItem("selectedDate", e.value);
  };

  const primarylocation = selectedLocation?.locationName;
  const statelocation = getLocationStateToken(primarylocation);
  const withoutstatelocation = getLocationNameWithoutState(primarylocation);

  return (
    <AppHeader
        headerContent={
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "flex-start",
              marginTop: "1rem",
            }}
          >
            {locationLoading ? (
              <CircularProgress color="inherit" size={30} />
            ) : (
              <div className="locationRow">
                {toTitleCase(withoutstatelocation ?? withoutstatelocation)}
                {(primarylocation === 'NA') || (statelocation.length !== 2) ? "" : <span>, {statelocation}</span>}
              </div>
            )}
            {options.length > 0 ? (
              <div className="summary-dropdown">
                <div className="blue-dropdown">
                  <SelectDropdown
                    style={{
                      fontSize: "1.6rem",
                      fontWeight: "500",
                      color: "#353535",
                      width: "19rem",
                      height: "3rem",
                      marginBottom: "1rem",
                    }}
                    styles={customStyles}
                    onChange={handleDateChange}
                    options={options}
                    value={selectedDate}
                    isSearchable={false}
                  />
                </div>
              </div>
            ) : (
              <CircularProgress color="inherit" size={30} />
            )}
          </div>
        }
      >
        {locationError.message ? (
          <ErrorMessage
            statusCode={locationError?.networkError?.statusCode}
            message={DATA_FETCH_ERROR}
          />
        ) : null}
        {activeStorechecker === false && selectedStoreId ? (
          <ErrorMessage message={ONLOAN_INVALID_STOREID_ERROR} />
        ) : null}
        <SalesTabManager />
        <BottomNavigator
          Label="Sales"
          RightRoute="/services-details/drivers"
          DisableLeft={true}
        />
      </AppHeader>
  );
};

SummaryDetails.propTypes = {
  employeeInfo: PropTypes.shape({
    adUpn: PropTypes.string,
  }),
  currentDateMatrix: PropTypes.func.isRequired,
};

const mapStateToProps = (state) => ({
  employeeInfo: state.auth.employeeInfo,
});
const mapDispatchToProps = (dispatch) => ({
  currentDateMatrix: (selectedDateMatrix) =>
    dispatch(setCurrentDateMatrix(selectedDateMatrix)),
});
export default connect(mapStateToProps, mapDispatchToProps)(SummaryDetails);
